import classNames from 'classnames';

import styles from './Fieldset.module.css';

import { visuallyHidden } from '../../styles';
import { Text } from '../Text';

type IntrinsicFieldsetProps = JSX.IntrinsicElements['fieldset'];

export interface FieldsetProps extends IntrinsicFieldsetProps {
  /* A style class to use for customizing the fieldset */
  className?: string;
  /* Optionally hide the legend element from being visible */
  hideLegend?: boolean;
  /* The label to show for the input fieldset */
  legend: string;
}

/**
 * Use the Fieldset component to group related form fields. Works as the
 * standard HTML `<fieldset>` element, with additional styles and the `legend`
 * prop.
 *
 * @example
 *
 * ```
 * export default function Example() {
 *   return (
 *     <Fieldset legend="Which recipes?">
 *       <RadioButton name="recipes" label="Turkey" />
 *       <RadioButton name="recipes" label="Beef" />
 *       <RadioButton name="recipes" label="Pork" />
 *     </Fieldset>
 *   );
 * }
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/fieldset
 */
export function Fieldset({
  children,
  className,
  hideLegend,
  legend,
}: FieldsetProps) {
  const legendClassName = classNames(hideLegend && visuallyHidden);

  return (
    <fieldset className={classNames(styles.fieldset, className)}>
      <Text
        variant="heading-22"
        as="legend"
        bold
        color="Kale3"
        className={legendClassName}
      >
        {legend}
      </Text>
      {children}
    </fieldset>
  );
}
